/**
 * Bottom Sheet Swipe-to-Dismiss Helper
 *
 * Provides swipe-to-dismiss functionality for mobile bottom sheet dialogs.
 * Handles touch events, scroll detection, and dismiss animations.
 *
 * Usage in Stimulus controller:
 *   import { BottomSheetSwipe } from "../helpers/bottom_sheet_swipe";
 *
 *   connect() {
 *     this.swipeHandler = new BottomSheetSwipe(this.modalTarget, {
 *       onDismiss: () => this.close({ confirmation: false }),
 *       canDismiss: () => !this.isLoadingState,
 *     });
 *   }
 *
 *   disconnect() {
 *     this.swipeHandler?.destroy();
 *   }
 */

const MOBILE_BREAKPOINT = 800; // matches $breakpoint-m (50em at 16px)
const DISMISS_THRESHOLD_PERCENT = 0.25; // 25% of dialog height
const VELOCITY_THRESHOLD = 0.5; // px/ms - fast swipe dismisses regardless of distance
const DRAG_HANDLE_ZONE = 60; // px - area at top where swipe always works
const DIRECTION_LOCK_THRESHOLD = 10; // px - movement needed to determine scroll vs swipe

export class BottomSheetSwipe {
  constructor(element, options = {}) {
    this.element = element;
    this.onDismiss = options.onDismiss || (() => {});
    this.canDismiss = options.canDismiss || (() => true);

    // State
    this.touchStartY = 0;
    this.touchStartX = 0;
    this.currentTranslateY = 0;
    this.isSwiping = false;
    this.isDeciding = false; // Determining if scroll or swipe
    this.touchStartTime = 0;
    this.touchStartedInHandle = false;
    this.animationFrame = null;
    this.pendingTimeouts = []; // Track setTimeout IDs for cleanup

    // Bound handlers for cleanup
    this._handleTouchStart = this.handleTouchStart.bind(this);
    this._handleTouchMove = this.handleTouchMove.bind(this);
    this._handleTouchEnd = this.handleTouchEnd.bind(this);

    // Only initialize on mobile
    if (this.isMobileBottomSheet()) {
      this.bindEvents();
    }

    // Re-check on resize
    this._handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this._handleResize);
  }

  /**
   * Check if we're on mobile and this is a bottom sheet dialog
   */
  isMobileBottomSheet() {
    return (
      window.innerWidth < MOBILE_BREAKPOINT &&
      !this.element.classList.contains("dialog__container--centered") &&
      !this.element.classList.contains("dialog__container--image")
    );
  }

  /**
   * Check if touch started in the drag handle zone (top area)
   */
  isTouchInHandleZone(touchY) {
    const rect = this.element.getBoundingClientRect();
    const relativeY = touchY - rect.top;
    return relativeY <= DRAG_HANDLE_ZONE;
  }

  /**
   * Bind touch event listeners
   */
  bindEvents() {
    this.element.addEventListener("touchstart", this._handleTouchStart, {
      passive: true,
    });
    this.element.addEventListener("touchmove", this._handleTouchMove, {
      passive: false,
    });
    this.element.addEventListener("touchend", this._handleTouchEnd);
    this.element.addEventListener("touchcancel", this._handleTouchEnd);
    this.eventsBound = true;
  }

  /**
   * Unbind touch event listeners
   */
  unbindEvents() {
    if (!this.eventsBound) return;

    this.element.removeEventListener("touchstart", this._handleTouchStart);
    this.element.removeEventListener("touchmove", this._handleTouchMove);
    this.element.removeEventListener("touchend", this._handleTouchEnd);
    this.element.removeEventListener("touchcancel", this._handleTouchEnd);
    this.eventsBound = false;
  }

  /**
   * Handle window resize - bind/unbind events based on viewport
   */
  handleResize() {
    if (this.isMobileBottomSheet() && !this.eventsBound) {
      this.bindEvents();
    } else if (!this.isMobileBottomSheet() && this.eventsBound) {
      this.unbindEvents();
    }
  }

  /**
   * Handle touch start
   */
  handleTouchStart(event) {
    // Cancel any pending animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    const touch = event.touches[0];
    this.touchStartY = touch.clientY;
    this.touchStartX = touch.clientX;
    this.touchStartTime = Date.now();
    this.touchStartedInHandle = this.isTouchInHandleZone(touch.clientY);
    this.currentTranslateY = 0;
    this.isSwiping = false;
    this.isDeciding = true; // Start decision phase
  }

  /**
   * Handle touch move
   */
  handleTouchMove(event) {
    if (!this.touchStartY) return;

    const touch = event.touches[0];
    const deltaY = touch.clientY - this.touchStartY;
    const deltaX = touch.clientX - this.touchStartX;

    // Decision phase: determine if this is a scroll or swipe
    if (this.isDeciding) {
      const totalMovement = Math.abs(deltaY) + Math.abs(deltaX);

      // Not enough movement to decide yet
      if (totalMovement < DIRECTION_LOCK_THRESHOLD) {
        return;
      }

      // Horizontal movement dominates = not a swipe
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this.resetSwipeState();
        return;
      }

      // Upward movement = scroll, not swipe
      if (deltaY < 0) {
        this.resetSwipeState();
        return;
      }

      // Downward movement - only allow swipe from handle zone
      // This prevents scroll/swipe conflicts in the content area
      if (!this.touchStartedInHandle) {
        this.resetSwipeState();
        return;
      }

      // Check if dismiss is allowed
      if (!this.canDismiss()) {
        this.resetSwipeState();
        return;
      }

      // Start swiping!
      this.isDeciding = false;
      this.isSwiping = true;
      this.element.setAttribute("data-swiping", "");

      // Recalculate start point from current position for smooth transition
      this.touchStartY = touch.clientY;
      this.currentTranslateY = 0;
    }

    // Apply transform while swiping
    if (this.isSwiping) {
      event.preventDefault(); // Prevent scroll while swiping

      const newDeltaY = touch.clientY - this.touchStartY;

      // Only allow positive (downward) movement
      this.currentTranslateY = Math.max(0, newDeltaY);

      // Use requestAnimationFrame for smooth animation
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }

      this.animationFrame = requestAnimationFrame(() => {
        // Use translate3d for GPU acceleration
        this.element.style.transform = `translate3d(0, ${this.currentTranslateY}px, 0)`;
      });
    }
  }

  /**
   * Handle touch end
   */
  handleTouchEnd() {
    // Cancel any pending animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    if (!this.isSwiping) {
      this.resetSwipeState();
      return;
    }

    const duration = Date.now() - this.touchStartTime;
    const velocity = duration > 0 ? this.currentTranslateY / duration : 0;
    const threshold = this.element.offsetHeight * DISMISS_THRESHOLD_PERCENT;

    // Dismiss if dragged past threshold OR fast swipe
    if (this.currentTranslateY > threshold || velocity > VELOCITY_THRESHOLD) {
      this.animateDismiss();
    } else {
      this.animateSnapBack();
    }

    this.element.removeAttribute("data-swiping");
    this.resetSwipeState();
  }

  /**
   * Reset swipe tracking state
   */
  resetSwipeState() {
    this.touchStartY = 0;
    this.touchStartX = 0;
    this.isSwiping = false;
    this.isDeciding = false;
    this.touchStartedInHandle = false;
  }

  /**
   * Animate dialog dismissal
   */
  animateDismiss() {
    // Calculate remaining distance to animate
    const remainingDistance =
      this.element.offsetHeight - this.currentTranslateY;
    // Faster animation for dismiss (150ms base, adjusted by remaining distance)
    const duration = Math.min(200, Math.max(100, remainingDistance * 0.3));

    this.element.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0, 0, 1)`;
    this.element.style.transform = "translate3d(0, 100%, 0)";

    const timeoutId = setTimeout(() => {
      this.element.style.transition = "";
      this.element.style.transform = "";
      this.onDismiss();
    }, duration);
    this.pendingTimeouts.push(timeoutId);
  }

  /**
   * Animate snap back to original position
   */
  animateSnapBack() {
    // iOS-like spring animation
    this.element.style.transition =
      "transform 250ms cubic-bezier(0.2, 1, 0.3, 1)";
    this.element.style.transform = "translate3d(0, 0, 0)";

    const timeoutId = setTimeout(() => {
      this.element.style.transition = "";
      this.element.style.transform = "";
    }, 250);
    this.pendingTimeouts.push(timeoutId);
  }

  /**
   * Clean up - call this in disconnect()
   */
  destroy() {
    // Cancel any pending animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    // Clear all pending timeouts to prevent memory leaks
    if (this.pendingTimeouts) {
      this.pendingTimeouts.forEach((id) => clearTimeout(id));
      this.pendingTimeouts = [];
    }

    this.unbindEvents();
    window.removeEventListener("resize", this._handleResize);
    this.element.style.transform = "";
    this.element.style.transition = "";
    this.element.removeAttribute("data-swiping");
  }
}
